import { create } from 'zustand';
import {
  DEFAULT_EFFECTIVE_ACCESS,
  EffectiveAccess,
  hasFeatureFlagPermission,
  hasModulePermission,
  hasPermission,
  PATH_TO_MODULE_KEY,
} from '../types/accessControl';
import { useAuthStore } from './authStore';

interface AccessControlState {
  getAccess: () => EffectiveAccess;
  can: (permissionKey: string) => boolean;
  canModule: (moduleKey: string) => boolean;
  canFeature: (featureFlagId: string) => boolean;
  canPath: (path: string) => boolean;
  isRestricted: () => boolean;
  isDataRestricted: () => boolean;
}

function accessFromUser(): EffectiveAccess {
  const user = useAuthStore.getState().user;
  return user?.effective_access ?? DEFAULT_EFFECTIVE_ACCESS;
}

export const useAccessControlStore = create<AccessControlState>(() => ({
  getAccess: () => accessFromUser(),
  can: (permissionKey: string) => hasPermission(accessFromUser(), permissionKey),
  canModule: (moduleKey: string) => hasModulePermission(accessFromUser(), moduleKey),
  canFeature: (featureFlagId: string) => hasFeatureFlagPermission(accessFromUser(), featureFlagId),
  canPath: (path: string) => {
    const base = path.split('?')[0].replace(/\/$/, '') || '/';
    // dashboard-builder routes
    if (base.startsWith('/dashboard-builder')) {
      return hasModulePermission(accessFromUser(), 'module.dashboardBuilder');
    }
    const moduleKey =
      PATH_TO_MODULE_KEY[base] ||
      Object.entries(PATH_TO_MODULE_KEY).find(([p]) => p !== '/' && base.startsWith(`${p}/`))?.[1];
    if (!moduleKey) return true;
    return hasModulePermission(accessFromUser(), moduleKey);
  },
  isRestricted: () => {
    const a = accessFromUser();
    return Boolean(a.restricted && !a.bypass);
  },
  isDataRestricted: () => {
    const a = accessFromUser();
    return Boolean(a.data_restricted && !a.bypass);
  },
}));

/** Hook-friendly selectors that re-render when auth user changes */
export function useEffectiveAccess(): EffectiveAccess {
  const user = useAuthStore((s) => s.user);
  return user?.effective_access ?? DEFAULT_EFFECTIVE_ACCESS;
}

export function useCanPermission(key: string): boolean {
  const access = useEffectiveAccess();
  return hasPermission(access, key);
}

export function useCanModule(moduleKey: string): boolean {
  const access = useEffectiveAccess();
  return hasModulePermission(access, moduleKey);
}
